/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/dvrdDatePicker.scss';
import {AwesomeIcon, ChangeFunction, ErrorType, voidFunction, WithBackground} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import classNames from 'classnames';
import React, {useEffect, useRef, useState} from 'react';

import {BolmateButton} from '../index';
import {useTranslation} from 'react-i18next';

interface Props {
    onChange: ChangeFunction<IDate>;
    closeOnChange?: boolean;
    value: IDate | null;
    label: string;
    error?: ErrorType;
    className?: string;
    placeholder?: string;
    dateFormat?: string;
    disabled?: boolean;
    min?: IDate | null;
    max?: IDate | null;
    allowBodyScroll?: boolean;
}

export default function DvrdDatePicker(props: Props) {
    const {
        onChange,
        className,
        closeOnChange,
        error,
        label,
        value,
        dateFormat,
        placeholder,
        disabled,
        max,
        min,
        allowBodyScroll
    } = props;
    const [pickerOpen, setPickerOpen] = useState(false);

    function onClickContainer() {
        if (!disabled) setPickerOpen(true);
    }

    const onChangePicker = (daySelected: boolean) => (value: IDate) => {
        if (min && value.isBefore(min, 'day')) return;
        if (max && value.isAfter(max, 'day')) return;
        onChange(value.clone());
        if (closeOnChange && daySelected) onClosePicker();
    };

    function onClosePicker() {
        setPickerOpen(false);
    }

    const format = dateFormat ?? 'YYYY-MM-DD';

    return (
        <div className={classNames('dvrd-date-picker', className, error && 'error', disabled && 'disabled')}>
            <label className='field-label'>{label}</label>
            <div className='value-container' onClick={onClickContainer}>
                <label className={classNames('value', !value && 'placeholder')}>
                    {value?.format(format) ?? placeholder ?? ''}
                </label>
                <AwesomeIcon name='calendar-alt' className='calendar-icon' />
            </div>
            <Picker
                onClose={onClosePicker}
                onChange={onChangePicker}
                open={pickerOpen}
                value={value}
                max={max}
                min={min}
                allowBodyScroll={allowBodyScroll}
            />
            <label className='error-label'>{error}</label>
        </div>
    );
}

interface PickerProps {
    onChange: (daySelected: boolean) => ChangeFunction<IDate>;
    onClose: VoidFunction;
    value: IDate | null;
    open: boolean;
    min?: IDate | null;
    max?: IDate | null;
    allowBodyScroll?: boolean;
}

function Picker(props: PickerProps) {
    const {onChange, open, onClose, min, max, allowBodyScroll} = props;
    const {t} = useTranslation();
    const ref = useRef<HTMLDivElement>(null);

    function getPickerValue(): IDate {
        if (!props.value && props.max) return props.max.clone();
        return new IDate(props.value);
    }

    function onReduce(key: 'year' | 'month') {
        return function () {
            const pickerValue = getPickerValue();
            pickerValue.add(-1, key);
            pickerValue.day(Math.min(pickerValue.daysInMonth(), pickerValue.day()));
            onChange(false)(pickerValue);
        };
    }

    function onAdd(key: 'year' | 'month') {
        return function () {
            const pickerValue = getPickerValue();
            pickerValue.add(1, key);
            pickerValue.day(Math.min(pickerValue.daysInMonth(), pickerValue.day()));
            onChange(false)(pickerValue);
        };
    }

    function onResetSwitcher(key: 'year' | 'month') {
        return function () {
            const pickerValue = getPickerValue();
            const now = IDate.now();
            if (key === 'year') pickerValue.year(now.year());
            else pickerValue.month(now.month());
            pickerValue.day(Math.min(pickerValue.daysInMonth(), pickerValue.day()));
            onChange(false)(pickerValue);
        };
    }

    function onClickToday() {
        onChange(true)(IDate.now());
    }

    function onSelectDay(day: number) {
        return function () {
            const pickerValue = getPickerValue();
            pickerValue.day(day);
            onChange(true)(pickerValue);
        };
    }

    function onSelectPadDay(day: number, kind: 'prev' | 'next') {
        return function () {
            const pickerValue = getPickerValue();
            const value = pickerValue.clone();
            if (kind === 'prev') value.add(-1, 'month');
            else value.add(1, 'month');
            value.day(day);
            onChange(true)(value);
        };
    }

    function keyListener(evt: KeyboardEvent) {
        const {key, shiftKey, ctrlKey} = evt;
        if (['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Enter'].includes(key)) {
            evt.preventDefault();
        }
        if (key === 'ArrowLeft') {
            onLeftKey(shiftKey, ctrlKey);
        } else if (key === 'ArrowRight') {
            onRightKey(shiftKey, ctrlKey);
        } else if (key === 'ArrowUp') {
            onUpKey();
        } else if (key === 'ArrowDown') {
            onDownKey();
        } else if (key === 'Enter') {
            onClose();
        }
    }

    function onLeftKey(shiftKey: boolean, ctrlKey: boolean) {
        const pickerValue = getPickerValue();
        if (shiftKey) {
            if (ctrlKey) onReduce('year')();
            else onReduce('month')();
        } else onChange(false)(pickerValue.add(-1, 'd'));
    }

    function onRightKey(shiftKey: boolean, ctrlKey: boolean) {
        const pickerValue = getPickerValue();
        if (shiftKey) {
            if (ctrlKey) onAdd('year')();
            else onAdd('month')();
        } else onChange(false)(pickerValue.add(1, 'd'));
    }

    function onUpKey() {
        const pickerValue = getPickerValue();
        onChange(false)(pickerValue.add(-7, 'd'));
    }

    function onDownKey() {
        const pickerValue = getPickerValue();
        onChange(false)(pickerValue.add(7, 'd'));
    }

    function addMountClass() {
        if (!ref.current?.classList.contains('switch-mount')) ref.current?.classList.add('switch-mount');
    }

    function removeMountClass() {
        ref.current?.classList.remove('switch-mount');
    }

    function renderSwitcher(key: 'year' | 'month', kind: 'add' | 'reduce') {
        const icon = kind === 'add' ? 'chevron-right' : 'chevron-left',
            title = kind === 'add' ? 'Volgende' : 'Vorige',
            onClick = kind === 'add' ? onAdd(key) : onReduce(key);
        return (
            <AwesomeIcon name={icon} title={title} className={classNames('switcher-icon', kind)} onClick={onClick} />
        );
    }

    function renderTooltipContainer() {
        return (
            <div className='tooltip-container'>
                <AwesomeIcon name='question-circle' className='tooltip-icon' prefix='far' />
                <div className='tooltip'>
                    <label className='tooltip-label'>Shortcuts</label>
                    <div className='key'>
                        <AwesomeIcon name='arrow-left' className='shortcut' />
                        <span className='directive'>-1 dag</span>
                    </div>
                    <div className='key'>
                        <div className='directives'>
                            <span className='shortcut text'>SHIFT</span>
                            <span className='directive'>+</span>
                            <AwesomeIcon name='arrow-left' className='shortcut' />
                        </div>
                        <span className='directive'>-1 maand</span>
                    </div>
                    <div className='key'>
                        <div className='directives full'>
                            <span className='shortcut text'>CTRL</span>
                            <span className='directive'>+</span>
                            <span className='shortcut text'>SHIFT</span>
                            <span className='directive'>+</span>
                            <AwesomeIcon name='arrow-left' className='shortcut' />
                        </div>
                        <span className='directive'>-1 jaar</span>
                    </div>
                    <div className='key'>
                        <AwesomeIcon name='arrow-right' className='shortcut' />
                        <span className='directive'>+1 dag</span>
                    </div>
                    <div className='key'>
                        <div className='directives'>
                            <span className='shortcut text'>SHIFT</span>
                            <span className='directive'>+</span>
                            <AwesomeIcon name='arrow-right' className='shortcut' />
                        </div>
                        <span className='directive'>+1 maand</span>
                    </div>
                    <div className='key'>
                        <div className='directives full'>
                            <span className='shortcut text'>CTRL</span>
                            <span className='directive'>+</span>
                            <span className='shortcut text'>SHIFT</span>
                            <span className='directive'>+</span>
                            <AwesomeIcon name='arrow-right' className='shortcut' />
                        </div>
                        <span className='directive'>+1 jaar</span>
                    </div>
                    <div className='key'>
                        <AwesomeIcon name='arrow-up' className='shortcut' />
                        <span className='directive'>-7 dagen</span>
                    </div>
                    <div className='key'>
                        <AwesomeIcon name='arrow-down' className='shortcut' />
                        <span className='directive'>+7 dagen</span>
                    </div>
                </div>
            </div>
        );
    }

    useEffect(() => {
        if (open) {
            document.addEventListener('keydown', keyListener);
            addMountClass();
            window.setTimeout(removeMountClass, 500);
        } else document.removeEventListener('keydown', keyListener);

        return function () {
            document.removeEventListener('keydown', keyListener);
        };
    }, [open]);

    const pickerValue = getPickerValue();
    const daysInMonth = pickerValue.daysInMonth();
    const days: number[] = [];
    const padBeforeDays: number[] = [];
    const padAfterDays: number[] = [];
    for (let i = 1; i <= daysInMonth; i++) days.push(i);
    let firstMonthDay = pickerValue.clone().day(1).weekday();
    if (!firstMonthDay) firstMonthDay = 7;
    const padDaysBefore = firstMonthDay - 1;
    let dayBefore = pickerValue.clone().add(-1, 'month').daysInMonth();
    for (let i = 0; i < padDaysBefore; i++) padBeforeDays.splice(0, 0, dayBefore--);

    const lastWeekDay = pickerValue.clone().day(daysInMonth).weekday();
    const padDaysAfter = 7 - lastWeekDay;
    for (let i = 0; i < padDaysAfter; i++) padAfterDays.push(i + 1);

    if (Math.ceil(padAfterDays.concat(days).concat(padBeforeDays).length / 7) < 6) {
        let dayAfter = padAfterDays[padAfterDays.length - 1] + 1;
        for (let i = 0; i < 7; i++) padAfterDays.push(dayAfter++);
    }

    return (
        <WithBackground active={open} onClose={onClose} allowBodyScroll={allowBodyScroll}>
            <div className='picker' ref={ref}>
                <div className='switcher year'>
                    {renderSwitcher('year', 'reduce')}
                    <label className='switcher-label' onClick={onResetSwitcher('year')}>
                        {pickerValue.year()}
                    </label>
                    {renderSwitcher('year', 'add')}
                </div>
                <div className='switcher month'>
                    {renderSwitcher('month', 'reduce')}
                    <label className='switcher-label' onClick={onResetSwitcher('month')}>
                        {pickerValue.format('MMMM')}
                    </label>
                    {renderSwitcher('month', 'add')}
                </div>
                <div className='current-date-container'>
                    <label className='current-date-label'>{pickerValue.format('dddd D MMMM YYYY')}</label>
                    {renderTooltipContainer()}
                </div>
                <div className='days-container'>
                    <div className='day no-select'>
                        <span className='day-label'>{t('controls.datePicker.dayShort.mon', 'Ma')}</span>
                    </div>
                    <div className='day no-select'>
                        <span className='day-label'>{t('controls.datePicker.dayShort.tue', 'Di')}</span>
                    </div>
                    <div className='day no-select'>
                        <span className='day-label'>{t('controls.datePicker.dayShort.wed', 'Wo')}</span>
                    </div>
                    <div className='day no-select'>
                        <span className='day-label'>{t('controls.datePicker.dayShort.thu', 'Do')}</span>
                    </div>
                    <div className='day no-select'>
                        <span className='day-label'>{t('controls.datePicker.dayShort.fri', 'Vr')}</span>
                    </div>
                    <div className='day no-select'>
                        <span className='day-label'>{t('controls.datePicker.dayShort.sat', 'Za')}</span>
                    </div>
                    <div className='day no-select'>
                        <span className='day-label'>{t('controls.datePicker.dayShort.sun', 'Zo')}</span>
                    </div>
                    {padBeforeDays.map((day: number, index: number) => {
                        return (
                            <div className={classNames('day', 'pad')} onClick={onSelectPadDay(day, 'prev')} key={index}>
                                <span className='day-label'>{day}</span>
                            </div>
                        );
                    })}
                    {days.map((day: number, index: number) => {
                        const isSelected = day === pickerValue.day(),
                            dayValue = pickerValue.clone().day(day);
                        const isDisabled = (min && dayValue.isBefore(min, 'd')) || (max && dayValue.isAfter(max, 'd'));
                        return (
                            <div
                                className={classNames(
                                    'day',
                                    isSelected && 'selected',
                                    day === 0 && 'hide',
                                    isDisabled && 'disabled'
                                )}
                                key={index}
                                onClick={isDisabled ? voidFunction : onSelectDay(day)}
                            >
                                <span className='day-label'>{day}</span>
                            </div>
                        );
                    })}
                    {padAfterDays.map((day: number, index: number) => {
                        return (
                            <div className={classNames('day', 'pad')} onClick={onSelectPadDay(day, 'next')} key={index}>
                                <span className='day-label'>{day}</span>
                            </div>
                        );
                    })}
                </div>
                <div className='actions-container'>
                    <BolmateButton
                        onClick={onClickToday}
                        label={t('controls.datePicker.today', 'Vandaag')}
                        className='action'
                    />
                    <BolmateButton
                        onClick={onClose}
                        label={t('controls.datePicker.close', 'Sluiten')}
                        secondary
                        className='action'
                    />
                </div>
            </div>
        </WithBackground>
    );
}
